import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Plus, Minus, ShoppingCart, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { RevealCard } from './PageTransitions';
import productsData from '../data/products.json';

interface Format {
  type: string;
  label: string;
  size: string;
  price: number;
}

interface Product {
  id: number;
  name: string;
  scent: string;
  tagline: string;
  description: string;
  ingredients: string[];
  benefits: string[];
  mood: string;
  color: string;
  formats: Format[];
  rating: number;
  inStock: boolean;
  featured?: boolean;
  bundleContents?: string[];
}

const Products: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [cart, setCart] = useState<{ [key: string]: number }>({});
  const [selectedFormats, setSelectedFormats] = useState<{ [key: number]: number }>({});
  const [expandedProduct, setExpandedProduct] = useState<number | null>(null);

  const products: Product[] = productsData.products as Product[];

  const getSelectedFormat = (product: Product): Format => {
    const idx = selectedFormats[product.id] ?? 0;
    return product.formats[idx];
  };

  const cartKey = (productId: number, formatType: string) => `${productId}-${formatType}`;

  const addToCart = (product: Product) => {
    const fmt = getSelectedFormat(product);
    const key = cartKey(product.id, fmt.type);
    setCart(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
  };

  const removeFromCart = (product: Product) => {
    const fmt = getSelectedFormat(product);
    const key = cartKey(product.id, fmt.type);
    setCart(prev => {
      const next = { ...prev };
      if (next[key] > 1) next[key] -= 1;
      else delete next[key];
      return next;
    });
  };

  const totalItems = Object.values(cart).reduce((s, n) => s + n, 0);
  const totalPrice = Object.entries(cart).reduce((sum, [key, qty]) => {
    const [idStr, type] = key.split('-');
    const product = products.find(p => p.id === parseInt(idStr, 10));
    const fmt = product?.formats.find(f => f.type === type);
    return sum + (fmt?.price ?? 0) * qty;
  }, 0);

  const moodLabels: Record<string, string> = {
    calm: 'Evening Calm',
    refresh: 'Midday Reset',
    focus: 'Morning Focus',
    bundle: 'All Day',
  };

  return (
    <section id="products" className="py-24 bg-parchment/50">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 max-w-2xl mx-auto"
        >
          <span className="inline-block text-herb-green text-sm font-semibold tracking-widest uppercase mb-4">
            The Collection
          </span>
          <h2 className="text-4xl md:text-5xl font-elegant font-bold text-forest mb-4">
            Signature Herb Blends
          </h2>
          <p className="text-warm-gray leading-relaxed">
            Three scents. Three moments. One apartment.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, index) => (
            <RevealCard key={product.id} delay={index * 0.1}>
              <motion.div
                className="bg-white rounded-2xl overflow-hidden border border-linen hover:border-herb-light/50 hover:shadow-lg transition-all duration-300 flex flex-col"
                whileHover={{ y: -4 }}
              >
                {/* Color header */}
                <div
                  className="h-3 w-full"
                  style={{ backgroundColor: product.color }}
                />

                <div className="p-6 flex flex-col flex-1">
                  {/* Mood tag */}
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className="text-xs font-semibold px-3 py-1 rounded-full text-white"
                      style={{ backgroundColor: product.color }}
                    >
                      {moodLabels[product.mood] || product.mood}
                    </span>
                    <div className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-amber-herb fill-current" />
                      <span className="text-xs text-warm-gray font-medium">{product.rating}</span>
                    </div>
                  </div>

                  {/* Name + scent */}
                  <h3 className="text-xl font-elegant font-bold text-forest mb-1">
                    {product.name}
                  </h3>
                  <p className="text-xs text-warm-gray font-italic italic mb-3 tracking-wide">
                    {product.scent}
                  </p>
                  <p className="text-xs text-warm-gray font-semibold mb-3">
                    "{product.tagline}"
                  </p>

                  {/* Description */}
                  <p className="text-sm text-charcoal/75 leading-relaxed mb-4">
                    {product.description}
                  </p>

                  {/* Expandable details */}
                  {product.id !== 4 && (
                    <div className="mb-4">
                      <button
                        onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
                        className="flex items-center gap-1 text-xs text-herb-green font-medium hover:text-forest transition-colors"
                      >
                        {expandedProduct === product.id ? (
                          <><ChevronUp className="w-3.5 h-3.5" /> Hide details</>
                        ) : (
                          <><ChevronDown className="w-3.5 h-3.5" /> Benefits & ingredients</>
                        )}
                      </button>

                      <motion.div
                        initial={false}
                        animate={{ height: expandedProduct === product.id ? 'auto' : 0, opacity: expandedProduct === product.id ? 1 : 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-3 space-y-2">
                          <div className="flex flex-wrap gap-1.5">
                            {product.benefits.map((b, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-mist text-herb-green rounded-full border border-herb-light/30">
                                {b}
                              </span>
                            ))}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {product.ingredients.map((ing, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-linen text-warm-gray rounded-full">
                                {ing}
                              </span>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  )}

                  {/* Bundle contents */}
                  {product.bundleContents && (
                    <div className="mb-4 bg-mist rounded-lg p-3">
                      <p className="text-xs font-semibold text-forest mb-1.5">Includes:</p>
                      {product.bundleContents.map((item, i) => (
                        <p key={i} className="text-xs text-warm-gray">• {item}</p>
                      ))}
                    </div>
                  )}

                  <div className="mt-auto">
                    {/* Format selector (not for bundle) */}
                    {product.formats.length > 1 && (
                      <div className="flex gap-1.5 mb-4">
                        {product.formats.map((fmt, fi) => (
                          <button
                            key={fmt.type}
                            onClick={() => setSelectedFormats(prev => ({ ...prev, [product.id]: fi }))}
                            className={`flex-1 text-xs py-1.5 px-2 rounded-lg border transition-all duration-200 ${
                              (selectedFormats[product.id] ?? 0) === fi
                                ? 'border-herb-green bg-herb-green text-white font-semibold'
                                : 'border-linen text-warm-gray hover:border-herb-sage'
                            }`}
                          >
                            {fmt.type === 'diffuser' ? 'Diffuser' : fmt.type === 'spray' ? 'Spray' : 'Candle'}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Price + cart */}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-forest">
                          ${getSelectedFormat(product).price}
                        </span>
                        <span className="text-xs text-warm-gray ml-1">
                          {getSelectedFormat(product).size}
                        </span>
                      </div>

                      {product.inStock ? (
                        <div className="flex items-center gap-2">
                          {cart[cartKey(product.id, getSelectedFormat(product).type)] > 0 && (
                            <div className="flex items-center gap-1">
                              <motion.button
                                whileTap={{ scale: 0.9 }}
                                onClick={() => removeFromCart(product)}
                                className="w-7 h-7 rounded-full bg-linen flex items-center justify-center text-forest hover:bg-herb-light/30 transition-colors"
                              >
                                <Minus className="w-3.5 h-3.5" />
                              </motion.button>
                              <span className="text-sm font-semibold w-4 text-center">
                                {cart[cartKey(product.id, getSelectedFormat(product).type)]}
                              </span>
                            </div>
                          )}
                          <motion.button
                            whileTap={{ scale: 0.9 }}
                            onClick={() => addToCart(product)}
                            className="w-8 h-8 rounded-full bg-herb-green text-white flex items-center justify-center hover:bg-forest transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </motion.button>
                        </div>
                      ) : (
                        <span className="text-sm text-warm-gray/60">Coming Soon</span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </RevealCard>
          ))}
        </div>

        {/* Cart summary */}
        {totalItems > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 right-6 bg-forest text-white px-5 py-3.5 rounded-2xl shadow-xl z-50 flex items-center gap-3"
          >
            <ShoppingCart className="w-5 h-5" />
            <div>
              <p className="text-sm font-semibold">{totalItems} item{totalItems !== 1 ? 's' : ''}</p>
              <p className="text-xs text-white/70">${totalPrice} total</p>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Products;
